/**
 * Supply structure instance resolver — FE mirror of the API lib.
 *
 * Keeps the same logic as teamform-api/src/groups/services/lib/supplyStructureInstance.js
 * so UI gates match server-side validation without a round-trip.
 *
 * See: teamform-api/docs/docs/taxonomy/supplyStructureInstance.md
 */

import { gql } from "@apollo/client";
import {
  isSupplyType,
  canModifySupplyTeamsForType,
} from "src/util/allocations";

// -----------------------------------------------------------------------
// Reason codes (mirrored from API supplyStructureInstanceReasonCodes.js)
// -----------------------------------------------------------------------

export const STRUCTURE_EDIT_REASON_CODES = {
  NO_INSTANCE_ANCESTOR: "NO_INSTANCE_ANCESTOR",
  EDITS_DISABLED: "EDITS_DISABLED",
  CROSS_INSTANCE: "CROSS_INSTANCE",
  ENHANCED_MOVE_MODE: "ENHANCED_MOVE_MODE",
  TARGET_NOT_SUPPLY: "TARGET_NOT_SUPPLY",
  INVALID_PARENT_TYPE: "INVALID_PARENT_TYPE",
  INSTANCE_ROOT_NOT_MOVABLE: "INSTANCE_ROOT_NOT_MOVABLE",
  NESTED_INSTANCE_ROOT: "NESTED_INSTANCE_ROOT",
  MUTEX_ENHANCED_MOVE: "MUTEX_ENHANCED_MOVE",
  ROOT_TYPE_NOT_SUPPLY: "ROOT_TYPE_NOT_SUPPLY",
  UNRESOLVED_ANCESTOR: "UNRESOLVED_ANCESTOR",
};

/**
 * Human-readable message for a reason code.
 *
 * @param {string} code
 * @param {{ rootName?: string, teamType?: string }} [context]
 * @returns {string}
 */
export const getStructureEditReasonMessage = (code, context = {}) => {
  switch (code) {
    case STRUCTURE_EDIT_REASON_CODES.NO_INSTANCE_ANCESTOR:
      return "This team is not part of a supply structure instance.";
    case STRUCTURE_EDIT_REASON_CODES.EDITS_DISABLED:
      return context.rootName
        ? `Structure edits are disabled on supply root "${context.rootName}". Enable them in the internal dashboard.`
        : "Structure edits are disabled on this supply structure root.";
    case STRUCTURE_EDIT_REASON_CODES.CROSS_INSTANCE:
      return "Teams can only be moved within the same supply structure instance.";
    case STRUCTURE_EDIT_REASON_CODES.ENHANCED_MOVE_MODE:
      return "Advanced Move mode is enabled — standard supply structure moves are disabled. Turn off Advanced Move in workspace settings.";
    case STRUCTURE_EDIT_REASON_CODES.TARGET_NOT_SUPPLY:
      return "Supply teams can only be moved under other supply teams.";
    case STRUCTURE_EDIT_REASON_CODES.INVALID_PARENT_TYPE:
      return "The target parent type does not allow this team type as a child.";
    case STRUCTURE_EDIT_REASON_CODES.INSTANCE_ROOT_NOT_MOVABLE:
      return "The supply structure root cannot be reparented via standard move.";
    case STRUCTURE_EDIT_REASON_CODES.NESTED_INSTANCE_ROOT:
      return "Cannot set a supply structure root when an ancestor or descendant already has that flag.";
    case STRUCTURE_EDIT_REASON_CODES.MUTEX_ENHANCED_MOVE:
      return "Cannot enable supply structure edits while Advanced Move is enabled, and vice versa. Disable one first.";
    case STRUCTURE_EDIT_REASON_CODES.ROOT_TYPE_NOT_SUPPLY:
      return context.teamType
        ? `Only supply teams can be a supply structure root (team type "${context.teamType}" is not a supply type).`
        : "Only supply teams can be a supply structure root.";
    case STRUCTURE_EDIT_REASON_CODES.UNRESOLVED_ANCESTOR:
      return "Could not verify the team's ancestors for nested-root validation. Please retry.";
    default:
      return "This action is not allowed on a supply structure instance.";
  }
};

// -----------------------------------------------------------------------
// Core resolver
// -----------------------------------------------------------------------

/**
 * Resolve the nearest ancestor (or self) that anchors a supply structure
 * instance for the given team.
 *
 * Prefers the server-resolved `supplyStructureRoot` field (batched on the
 * `Group` type) so UI gates match server-side enforcement even when the org
 * chart only holds a partial ancestor map. Falls back to walking hierarchyIds
 * for cached/older data that predates the field.
 *
 * @param {string} teamId
 * @param {{ [id: string]: object }} teamsById
 * @returns {{ rootId: string, rootTeam: object, enableEdits: boolean } | null}
 */
export const findSupplyStructureRoot = (teamId, teamsById) => {
  const team = teamsById[teamId];
  if (!team) return null;

  // Preferred: server-resolved nearest instance root (null when not under one).
  if (team.supplyStructureRoot !== undefined) {
    const root = team.supplyStructureRoot;
    if (!root?.id) return null;
    return {
      rootId: root.id,
      rootTeam: root,
      enableEdits: !!root.enableSupplyStructureEdits,
    };
  }

  // Fallback: check self first, then walk hierarchyIds from nearest (last) to root (first).
  const chainIds = [teamId, ...[...(team.hierarchyIds || [])].reverse()];

  for (const id of chainIds) {
    const t = teamsById[id];
    if (t && t.isRootForSource) {
      return {
        rootId: id,
        rootTeam: t,
        enableEdits: !!t.enableSupplyStructureEdits,
      };
    }
  }
  return null;
};

/**
 * @param {string} teamId
 * @param {{ [id: string]: object }} teamsById
 * @returns {boolean}
 */
export const isUnderSupplyStructureInstance = (teamId, teamsById) =>
  findSupplyStructureRoot(teamId, teamsById) !== null;

/**
 * Core gate for any supply structure edit on the FE.
 *
 * Operations: "MOVE", "ADD_CHILD", "REMOVE", "DROP_TARGET", "ADVANCED_MOVE"
 *
 * @param {{
 *   teamId: string,
 *   targetParentId?: string | null,
 *   teamsById: { [id: string]: object },
 *   groupTypes: object,
 *   enableEnhancedMove: boolean,
 *   operation: "MOVE" | "ADD_CHILD" | "REMOVE" | "DROP_TARGET" | "ADVANCED_MOVE",
 * }} params
 * @returns {{ allowed: boolean, code?: string, message?: string }}
 */
export const canEditSupplyStructure = ({
  teamId,
  targetParentId,
  teamsById,
  groupTypes,
  enableEnhancedMove,
  operation,
}) => {
  const deny = (code, context = {}) => ({
    allowed: false,
    code,
    message: getStructureEditReasonMessage(code, context),
  });

  const team = teamsById[teamId];
  if (!team) return { allowed: true };

  if (operation === "ADVANCED_MOVE") {
    if (isUnderSupplyStructureInstance(teamId, teamsById)) {
      return deny(STRUCTURE_EDIT_REASON_CODES.INSTANCE_ROOT_NOT_MOVABLE);
    }
    return { allowed: true };
  }

  const subjectInstance = findSupplyStructureRoot(teamId, teamsById);

  if (!subjectInstance) {
    return { allowed: true };
  }

  const { rootId, rootTeam, enableEdits } = subjectInstance;

  if (
    teamId === rootId &&
    (operation === "MOVE" || operation === "DROP_TARGET")
  ) {
    return deny(STRUCTURE_EDIT_REASON_CODES.INSTANCE_ROOT_NOT_MOVABLE);
  }

  if (enableEnhancedMove) {
    return deny(STRUCTURE_EDIT_REASON_CODES.ENHANCED_MOVE_MODE);
  }

  if (!enableEdits) {
    return deny(STRUCTURE_EDIT_REASON_CODES.EDITS_DISABLED, {
      rootName: rootTeam.name,
    });
  }

  if ((operation === "MOVE" || operation === "DROP_TARGET") && targetParentId) {
    const targetInstance = findSupplyStructureRoot(targetParentId, teamsById);

    if (!targetInstance || targetInstance.rootId !== rootId) {
      return deny(STRUCTURE_EDIT_REASON_CODES.CROSS_INSTANCE);
    }

    const teamGroupType =
      typeof team.type === "string" ? groupTypes?.[team.type] : null;
    const isTeamSupply = isSupplyType(teamGroupType);
    if (isTeamSupply) {
      const targetTeam = teamsById[targetParentId];
      const targetGroupType =
        targetTeam && typeof targetTeam.type === "string"
          ? groupTypes?.[targetTeam.type]
          : null;
      if (!isSupplyType(targetGroupType)) {
        return deny(STRUCTURE_EDIT_REASON_CODES.TARGET_NOT_SUPPLY);
      }
    }
  }

  return { allowed: true };
};

/**
 * Convenience: get just the reason string for a blocked action, or null if allowed.
 *
 * @param {Parameters<typeof canEditSupplyStructure>[0]} params
 * @returns {string | null}
 */
export const getStructureEditBlockReason = (params) => {
  const result = canEditSupplyStructure(params);
  return result.allowed ? null : result.message || null;
};

// -----------------------------------------------------------------------
// Shared "hide supply structure actions" gate
//
// Single source of truth for "is this team's structure locked against edits
// (add child / move / remove) by supply-structure rules". Used by both the
// team-card action menu and the org chart canvas "+" add-team affordance so
// the two surfaces can't drift out of sync with each other.
// -----------------------------------------------------------------------

export const SUPPLY_ROOT_CACHE_FRAGMENT = gql`
  fragment SupplyRootCacheFields on Group {
    id
    isRootForSource
    enableSupplyStructureEdits
  }
`;

/**
 * Build the `teamsById` map that `findSupplyStructureRoot` needs, from a team
 * plus any ancestor groups already in the Apollo cache (populated when the
 * org chart was visited). Falls back to self-only if ancestors are not
 * cached — correctly handles the case where this team is itself the root.
 *
 * `team`'s own entry is always the full object passed in and is never
 * replaced by a cache lookup — some ancestor-id lists (e.g. hierarchyIds)
 * include the team's own id, and the cache fragment only carries a few
 * fields, so overwriting it would erase `hierarchyIds`/`supplyStructureRoot`
 * and make an instance-backed team look legacy.
 *
 * @param {object} team
 * @param {string[]} ancestorIds
 * @param {{ readFragment: (options: unknown) => unknown } | undefined | null} apolloClient
 * @returns {{ [id: string]: object }}
 */
export const buildAncestorTeamsById = (team, ancestorIds, apolloClient) => {
  const teamsById = { [team.id]: team };
  if (typeof apolloClient?.readFragment !== "function") {
    return teamsById;
  }
  for (const ancestorId of ancestorIds || []) {
    if (ancestorId === team.id) continue;
    try {
      const cached = apolloClient.readFragment({
        id: `Group:${ancestorId}`,
        fragment: SUPPLY_ROOT_CACHE_FRAGMENT,
      });
      if (cached) teamsById[cached.id] = cached;
    } catch {
      // Not in cache — skip; we'll get a conservative result.
    }
  }
  return teamsById;
};

/**
 * Should supply-structure-gated actions (add child / move / remove) be
 * hidden for this team?
 *
 * - Any team under a supply structure instance (checked first, regardless of
 *   its own type): gated on the instance root's `enableSupplyStructureEdits`
 *   flag, mutex'd against Advanced Move mode.
 * - Non-instance-backed supply teams: gated on the team type's
 *   `canModifySupplyTeamsForType` flag.
 * - Non-instance-backed, non-supply teams (e.g. demand): never hidden.
 *
 * @param {object} team
 * @param {object} groupTypes
 * @param {{ teamsById: { [id: string]: object }, enableEnhancedMove: boolean }} params
 * @returns {boolean}
 */
export const resolveHideSupplyStructureActions = (
  team,
  groupTypes,
  { teamsById, enableEnhancedMove }
) => {
  const config = groupTypes?.[team?.type ?? ""] ?? {};
  const isSupplyTeam = isSupplyType(config);

  if (isUnderSupplyStructureInstance(team?.id, teamsById)) {
    return !canEditSupplyStructure({
      teamId: team?.id,
      teamsById,
      groupTypes,
      enableEnhancedMove,
      operation: "ADD_CHILD",
    }).allowed;
  }

  if (isSupplyTeam) {
    return !canModifySupplyTeamsForType(groupTypes, team?.type);
  }

  return false;
};
